import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, RefreshControl, StyleSheet, View } from 'react-native';

import { Chip, EmptyState, SkeletonRow, Text } from '~/components/core';
import { Feather, Ionicons } from '~/components/core/Icons';
import ConfirmationMessage from '~/components/features/ConfirmationMessage';
import { Strategy } from '~/api-services/AIStrategyBuilderService/types';
import StreamingService from '~/api-services/StreamingService';
import { StrategyEvent } from '~/api-services/StreamingService/types';
import { periodLabel, statusLabel } from '~/constants/strategyConstants';
import i18n from '~/i18n';
import { useAppSelector, useOnboarding, useStyles } from '~/hooks';
import { symbolsSelector } from '~/state/selectors';
import { scaleSize } from '~/constants/scale';
import { FONT_SIZE_XS } from '~/constants/typography';
import { Theme } from '~/theme';

import { useStrategies } from './hooks/useStrategies';
import { buildInstrumentOptions } from './utils/instruments';
import { setStrategyPreview } from './utils/previewCache';
import { parseSort, sortDirLabelKey, sortFieldLabelKey } from './utils/sort';
import QuickActionsSheet from './components/QuickActionsSheet';
import CreateChooserSheet from './components/list/CreateChooserSheet';
import StrategyFilterSheet from './components/list/StrategyFilterSheet';
import StrategyIntroSheet from './components/list/StrategyIntroSheet';
import CustomHeader from '~/components/navigators/CustomHeader';
import StrategyIdentityRow from './components/StrategyIdentityRow';

// Bump the version to show the intro again after a material rewrite of its copy.
const INTRO_KEY = 'aiStrategy';
const INTRO_VERSION = 1;

type Props = {
    onOpenStrategy: (strategy: Strategy) => void;
    onCreate: (templateUuid?: string) => void;
    onClone: (strategyId: string) => void;
};

// Lets the host re-run the quiet refresh when the list becomes visible again — a stack focus
// event on mobile, a pane switch on desktop. The list does not know which it is.
export type AIStrategiesListHandle = { refresh: () => void };

function AIStrategiesList({ onOpenStrategy, onCreate, onClone }: Props, ref: React.Ref<AIStrategiesListHandle>) {
    const styles = useStyles(stylesGenerator);
    const symbolsMap = useAppSelector(symbolsSelector);
    const {
        strategies,
        loading,
        loadingMore,
        error,
        filters,
        setFilters,
        refresh,
        refreshQuietly,
        loadMore,
        patchStrategy,
        enable,
        disable,
        discard,
        archive,
        unarchive,
    } = useStrategies();

    const [filterOpen, setFilterOpen] = useState(false);
    const [chooserOpen, setChooserOpen] = useState(false);
    const [introOpen, setIntroOpen] = useState(false);
    const [actionsFor, setActionsFor] = useState<Strategy | null>(null);
    const [toast, setToast] = useState<{ text: string; isError: boolean } | null>(null);

    const activeFilterCount = [
        filters.status,
        filters.instrument_code,
        filters.instrument_period,
        filters.side,
        filters.sort,
        filters.archivedOnly,
    ].filter(Boolean).length;

    // First run on this device opens the intro; the header icon reopens it any time after.
    const { shouldShow: introUnseen, dismiss: markIntroSeen } = useOnboarding(INTRO_KEY, INTRO_VERSION);

    useEffect(() => {
        if (introUnseen) {
            setIntroOpen(true);
        }
    }, [introUnseen]);

    const closeIntro = useCallback(() => {
        setIntroOpen(false);
        // Idempotent, so reopening from the header and closing again costs one no-op write.
        markIntroSeen();
    }, [markIntroSeen]);

    // The app-standard header (CustomHeader). The list is its tab's root, so no back button; the
    // title is left-aligned (it reads as a page/section title, and there is no back button to
    // balance a centred one against) with the info/filter/create actions on the right.
    const header = (
        <CustomHeader
            title="AI Strategies"
            noTranslateTitle
            titleAlign="left"
            hideBackButton
            right={
                <View style={styles.headerRightRow}>
                    <Pressable
                        hitSlop={8}
                        onPress={() => setIntroOpen(true)}
                        style={styles.headerButton}
                        accessibilityLabel="About AI Strategies"
                    >
                        <Ionicons name="information-circle-outline" size={scaleSize(19)} style={styles.headerIcon} />
                    </Pressable>
                    <Pressable
                        hitSlop={8}
                        onPress={() => setFilterOpen(true)}
                        style={styles.headerButton}
                        accessibilityLabel="Filters"
                    >
                        <Ionicons name="funnel-outline" size={scaleSize(18)} style={styles.headerIcon} />
                        {activeFilterCount > 0 ? (
                            <View style={styles.badge}>
                                <Text noTranslate style={styles.badgeText}>
                                    {String(activeFilterCount)}
                                </Text>
                            </View>
                        ) : null}
                    </Pressable>
                    <Pressable
                        hitSlop={8}
                        onPress={() => setChooserOpen(true)}
                        style={styles.headerButton}
                        accessibilityLabel="New strategy"
                    >
                        <Feather name="plus" size={scaleSize(24)} style={styles.headerIcon} />
                    </Pressable>
                </View>
            }
        />
    );

    // Quietly: the visible spinner belongs to pull-to-refresh, and on iOS showing it here drops
    // the whole list by the spinner's height.
    useImperativeHandle(ref, () => ({ refresh: refreshQuietly }), [refreshQuietly]);

    // Live row updates: the server pushes progress/status per strategy on the account's room.
    useEffect(() => {
        const cb = (e: StrategyEvent) => {
            if (!e.id) {
                return;
            }
            const patch: Partial<Strategy> = {};
            if (typeof e.progress === 'number') {
                patch.progress_percent = e.progress;
            }
            if (e.status) {
                patch.status = e.status as Strategy['status'];
            }
            if (Object.keys(patch).length > 0) {
                patchStrategy(e.id, patch);
            }
        };
        StreamingService.subscribeStrategyEvent(cb);
        return () => {
            StreamingService.unsubscribeStrategyEvent(cb);
        };
    }, [patchStrategy]);

    // Instrument filter options — every streamed symbol, same builder as the wizard's picker.
    const instrumentOptions = useMemo(() => buildInstrumentOptions(symbolsMap), [symbolsMap]);

    const notify = useCallback((text: string, isError: boolean) => setToast({ text, isError }), []);

    const openDetails = useCallback(
        // Stash the tapped row for the detail header (shown instantly instead of a placeholder while
        // the full strategy loads) via an in-memory slot, not a nav param — a param would serialize
        // into the URL as `?preview=[object Object]` and arrive as a string on reload.
        (strategy: Strategy) => {
            setStrategyPreview(strategy);
            onOpenStrategy(strategy);
        },
        [onOpenStrategy]
    );

    const openActions = useCallback((strategy: Strategy) => setActionsFor(strategy), []);

    // Stable renderItem + memoized rows: this component re-renders on every quote batch
    // (symbolsMap subscription above), and per-item arrow props would defeat the row memo.
    const renderItem = useCallback(
        ({ item }: { item: Strategy }) => (
            <StrategyIdentityRow strategy={item} onPress={openDetails} onActionsPress={openActions} />
        ),
        [openDetails, openActions]
    );

    const startCreate = (templateUuid?: string) => {
        setChooserOpen(false);
        onCreate(templateUuid);
    };

    const firstLoad = loading && strategies.length === 0;

    return (
        <>
            {header}
            {activeFilterCount > 0 ? (
                <View style={styles.activeFiltersRow}>
                    {filters.status ? (
                        <Chip
                            label={`${statusLabel(filters.status)} ✕`}
                            selected
                            size="sm"
                            onPress={() => setFilters({ ...filters, status: undefined })}
                            containerStyle={styles.activeFilterChip}
                        />
                    ) : null}
                    {filters.instrument_code ? (
                        <Chip
                            label={`${
                                symbolsMap[filters.instrument_code]?.nameTranslated ?? filters.instrument_code
                            } ✕`}
                            selected
                            size="sm"
                            onPress={() => setFilters({ ...filters, instrument_code: undefined })}
                            containerStyle={styles.activeFilterChip}
                        />
                    ) : null}
                    {filters.instrument_period ? (
                        <Chip
                            label={`${periodLabel(filters.instrument_period)} ✕`}
                            selected
                            size="sm"
                            onPress={() => setFilters({ ...filters, instrument_period: undefined })}
                            containerStyle={styles.activeFilterChip}
                        />
                    ) : null}
                    {filters.side ? (
                        <Chip
                            label={`${i18n.t(filters.side)} ✕`}
                            selected
                            size="sm"
                            onPress={() => setFilters({ ...filters, side: undefined })}
                            containerStyle={styles.activeFilterChip}
                        />
                    ) : null}
                    {filters.archivedOnly ? (
                        <Chip
                            label={`${i18n.t('ai-strategy-archived')} ✕`}
                            selected
                            size="sm"
                            onPress={() => setFilters({ ...filters, archivedOnly: undefined })}
                            containerStyle={styles.activeFilterChip}
                        />
                    ) : null}
                    {filters.sort ? (
                        <Chip
                            label={`${i18n.t(sortFieldLabelKey(parseSort(filters.sort).field))} · ${i18n.t(
                                sortDirLabelKey(parseSort(filters.sort).field, parseSort(filters.sort).dir)
                            )} ✕`}
                            selected
                            size="sm"
                            onPress={() => setFilters({ ...filters, sort: undefined })}
                            containerStyle={styles.activeFilterChip}
                        />
                    ) : null}
                </View>
            ) : null}

            {firstLoad ? (
                <View style={styles.skeletons}>
                    {[0, 1, 2, 3].map((i) => (
                        <View key={i} style={styles.skeletonRow}>
                            <SkeletonRow height={16} width="55%" />
                            <SkeletonRow height={11} width="35%" />
                        </View>
                    ))}
                </View>
            ) : (
                <FlatList
                    style={styles.list}
                    data={strategies}
                    keyExtractor={(item) => item.id}
                    initialNumToRender={20}
                    onEndReachedThreshold={0.4}
                    onEndReached={loadMore}
                    refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} />}
                    renderItem={renderItem}
                    ListFooterComponent={
                        loadingMore ? (
                            <View style={styles.footerLoading}>
                                <ActivityIndicator />
                            </View>
                        ) : null
                    }
                    ListEmptyComponent={
                        error ? (
                            <EmptyState
                                title="ai-strategy-list-error-title"
                                body={error}
                                ctaLabel="retry"
                                onCtaPress={refresh}
                            />
                        ) : activeFilterCount > 0 ? (
                            <EmptyState
                                title="ai-strategy-list-filtered-empty"
                                ctaLabel="reset-filters"
                                onCtaPress={() => setFilters({})}
                            />
                        ) : (
                            <EmptyState
                                icon="expand"
                                title="ai-strategy-list-empty-title"
                                body="ai-strategy-list-empty-body"
                                ctaLabel="ai-strategy-list-empty-cta"
                                onCtaPress={() => setChooserOpen(true)}
                            />
                        )
                    }
                />
            )}

            <StrategyIntroSheet visible={introOpen} onClose={closeIntro} />

            <StrategyFilterSheet
                visible={filterOpen}
                onClose={() => setFilterOpen(false)}
                filters={filters}
                onApply={setFilters}
                instrumentOptions={instrumentOptions}
            />

            <CreateChooserSheet visible={chooserOpen} onClose={() => setChooserOpen(false)} onChoose={startCreate} />

            <QuickActionsSheet
                strategy={actionsFor}
                onClose={() => setActionsFor(null)}
                onView={(s) => {
                    setActionsFor(null);
                    openDetails(s);
                }}
                onClone={(s) => onClone(s.id)}
                actions={{ enable, disable, discard, archive, unarchive }}
                archived={!!filters.archivedOnly}
                notify={notify}
            />

            {toast ? (
                <ConfirmationMessage
                    visible
                    text={toast.text}
                    error={toast.isError}
                    setVisible={() => setToast(null)}
                />
            ) : null}
        </>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        list: {
            flex: 1,
        },
        headerRightRow: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        headerButton: {
            paddingHorizontal: scaleSize(8),
        },
        headerIcon: {
            color: theme.text,
        },
        badge: {
            position: 'absolute',
            top: scaleSize(-6),
            right: scaleSize(0),
            backgroundColor: theme.button,
            borderRadius: scaleSize(8),
            minWidth: scaleSize(15),
            height: scaleSize(15),
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: scaleSize(3),
        },
        badgeText: {
            fontSize: FONT_SIZE_XS,
            color: theme.white,
        },
        activeFiltersRow: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            paddingHorizontal: scaleSize(12),
            paddingTop: scaleSize(8),
        },
        activeFilterChip: {
            marginRight: scaleSize(6),
            marginBottom: scaleSize(4),
        },
        skeletons: {
            paddingHorizontal: scaleSize(14),
            paddingTop: scaleSize(14),
        },
        skeletonRow: {
            marginBottom: scaleSize(14),
        },
        footerLoading: {
            paddingVertical: scaleSize(16),
        },
    });

export default forwardRef(AIStrategiesList);
